import { useState, useEffect } from 'react';
import { Modal } from 'antd';
import {
  Button,
  CloseIcon,
  Dropdown,
  Input,
  Subtitle,
  Spinner,
} from '../../components/shared';
import { useDispatch, useSelector } from 'react-redux';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import FlupsySectionsModal from '../../components/view-modals/FlupsySectionsModal';
import { showFeedback } from '../../store/ui/ui.actions';
import { sendSingleRequest } from '../../apis';
import ModalFeedbackView from '../../components/shared/feedback/ModalFeedbackView';
import { TBusinessType } from '../../entities/general.entities';
import SelectCropType from '../../components/farmform/SelectCropType';

const basketsPerSectionOptions = [
  { id: '4', label: '4', value: '4' },
  { id: '6', label: '6', value: '6' },
  { id: '8', label: '8', value: '8' },
];

interface Props {
  visible: boolean;
  onCancel: () => void;
  cropType?: TBusinessType;
}

interface BucketsData {
  lines: any;
  farm_id: number;
}

const FlupsyModal = ({
  visible,
  onCancel,
  cropType: initialCropType,
}: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const [basketsPerSection, setBasketsPerSection] = useState(0);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [bucketWidth, setBucketWidth] = useState(0);
  const [bucketHeight, setBucketHeight] = useState(0);
  const [showFlupsySectionsModal, setShowFlupsySectionsModal] = useState(false);
  const [cropType, setCropType] = useState<TBusinessType>(
    initialCropType ?? 'MUSSEL',
  );
  const [disabled, setDisabled] = useState(false);
  const [bucketsData, setBucketsData] = useState<BucketsData | null>(null);
  const [existingData, setExistingData] = useState<any>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFlupsy();
  }, []);

  const fetchFlupsy = async () => {
    const response = await sendSingleRequest(
      {},
      'GET',
      'api/farm/get-flupsy',
      true,
    );
    if (response.status) {
      setExistingData(response.data);
      if (response.data.length === 0) {
        setShowCreateForm(true);
      }
    }
    setLoading(false);
  };

  const validForm = () => {
    if (![4, 6, 8].includes(basketsPerSection)) {
      dispatch(
        showFeedback({
          type: 'error',
          isMessageModal: true,
          message: translate(
            lang,
            'Please select the number of buckets per section',
          ),
        }),
      );
      return null;
    }
    if (bucketWidth < 1) {
      dispatch(
        showFeedback({
          type: 'error',
          isMessageModal: true,
          message: translate(lang, '_enter_height_raft'),
        }),
      );
      return null;
    }
    if (bucketHeight < 1) {
      dispatch(
        showFeedback({
          type: 'error',
          isMessageModal: true,
          message: translate(lang, '_enter_length_raft'),
        }),
      );
      return null;
    }

    return true;
  };

  const confirmClick = async () => {
    const formData = validForm();
    if (!formData) return;
    setDisabled(true);
    const response = await sendSingleRequest(
      {
        buckets: basketsPerSection,
        height: bucketHeight,
        length: bucketWidth,
        crop_type: cropType,
      },
      'POST',
      'api/farm/create-flupsy',
      true,
    );

    if (response.status) {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, response.data?.message ?? 'Success'),
        }),
      );

      setBucketsData({
        lines: response.data?.lines,
        farm_id: response.data?.farm.id,
      });
      setShowFlupsySectionsModal(true);
      setDisabled(false);
    } else {
      dispatch(
        showFeedback({
          isMessageModal: true,
          type: 'error',
          message: translate(lang, response.data?.message ?? 'Server error'),
        }),
      );
      setDisabled(false);
    }
  };

  const selectExisting = async (e: any, index: number) => {
    e.preventDefault();
    setBucketsData({
      lines: existingData[index].lines,
      farm_id: existingData[index].farm.id,
    });

    setBucketHeight(existingData[index].farm.metadata.height);
    setBucketWidth(existingData[index].farm.metadata['length']);
    setBasketsPerSection(existingData[index].farm.metadata.buckets);
    setShowFlupsySectionsModal(true);
  };

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      closable
      closeIcon={<CloseIcon />}
    >
      <div className='wrap'>
        {loading ? (
          <div>
            <Spinner />
          </div>
        ) : (
          <>
            {existingData.length > 0 && (
              <>
                <div className='align-center mb-16'>
                  <Subtitle
                    color='black-1'
                    align='left'
                    size={1}
                    fontWeight={600}
                  >
                    {translate(lang, 'Existing Flupsy')}
                  </Subtitle>
                </div>
                <div className='mb-32'>
                  {existingData.map((data: any, index: number) => (
                    <div key={index} className='existing-flupsy'>
                      <button
                        className='button button--3 button--green button--small button--bordered'
                        onClick={e => selectExisting(e, index)}
                      >
                        {data.farm.name + ' - ' + data.farm.id}
                      </button>
                    </div>
                  ))}
                </div>
                {!showCreateForm && (
                  <Button
                    width={'small'}
                    size={2}
                    type='fill'
                    color='green'
                    className='rsp-btn ml-auto'
                    onClick={() => setShowCreateForm(true)}
                    disabled={disabled}
                  >
                    {translate(lang, 'Create New')}
                  </Button>
                )}
              </>
            )}
            {showCreateForm && (
              <>
                <div className='d-flex align-center mb-32'>
                  <Subtitle
                    color='black-1'
                    align='left'
                    size={1}
                    fontWeight={600}
                  >
                    {translate(lang, 'Bay Spat Storage')}
                  </Subtitle>
                </div>
                <div className='mb-17'>
                  <SelectCropType
                    value={cropType}
                    onChange={v => v && setCropType(v)}
                  />
                </div>
                <div className='spat-storage-modal'>
                  <div className='mb-17'>
                    <p>
                      <b>Section</b> is a section of your flupsy containint
                      multiple bucket. How many buckets you have per section?
                    </p>
                    <Dropdown
                      onChange={v => setBasketsPerSection(parseInt(v))}
                      label={translate(lang, 'Select buckets')}
                      options={basketsPerSectionOptions}
                      value={basketsPerSection.toString()}
                    />
                  </div>
                  <div className={`mb-17`}>
                    <p>
                      How big is your raft in terms of sections where X is
                      height and Y is length. For instance 4x by 4y would give
                      you 16 sections (remember each section also has multiple
                      baskets)
                    </p>
                  </div>
                  <div className='mb-17'>
                    <Input
                      type='number'
                      label={translate(lang, 'Height')}
                      value={bucketHeight.toString() ?? ''}
                      onChange={e => setBucketHeight(parseInt(e.target.value))}
                    />
                  </div>
                  <div className='mb-17'>
                    <Input
                      type='number'
                      label={translate(lang, 'Length')}
                      value={bucketWidth.toString() ?? ''}
                      onChange={e => setBucketWidth(parseInt(e.target.value))}
                    />
                  </div>
                  <Button
                    width={'small'}
                    size={2}
                    type='fill'
                    color='green'
                    className='rsp-btn ml-auto'
                    onClick={() => confirmClick()}
                    disabled={disabled}
                  >
                    {translate(lang, 'Confirm')}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>
      {showFlupsySectionsModal && (
        <FlupsySectionsModal
          bucketsX={bucketWidth}
          bucketsY={bucketHeight}
          divisions={basketsPerSection}
          bucketsData={bucketsData}
          onCancel={() => setShowFlupsySectionsModal(false)}
        />
      )}
      <ModalFeedbackView />
    </Modal>
  );
};

export default FlupsyModal;
